import { Info, Minus, Plus } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useRef, useState } from 'react';

import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogDescription } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { safelyRender } from '@/utils/safelyRender';

type Props = {
    catalogId: number;
    onReadMoreClick?: () => void;
};

export const CatalogDetailsZoneMobileDescription = ({ catalogId, onReadMoreClick }: Props) => {
    const formatMessage = useAppFormatMessage();
    const description = useAppSelector((state) => getCatalogDescription(state, catalogId));
    const [isExpanded, setIsExpanded] = useState(false);
    const [isClamped, setIsClamped] = useState(false);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLDivElement>(null);

    useIsomorphicLayoutEffect(() => {
        const element = textRef.current;
        if (!element || isExpanded) {
            return undefined;
        }

        const updateIsClamped = () => {
            setIsClamped(element.scrollHeight > element.clientHeight);
        };

        updateIsClamped();
        const resizeObserver = new ResizeObserver(updateIsClamped);
        resizeObserver.observe(element);
        return () => {
            resizeObserver.disconnect();
        };
    }, [description, isExpanded]);

    const handleToggle = () => {
        if (onReadMoreClick) {
            onReadMoreClick();
            return;
        }

        const nextIsExpanded = !isExpanded;
        setIsExpanded(nextIsExpanded);

        if (!nextIsExpanded && descriptionRef.current) {
            const rect = descriptionRef.current.getBoundingClientRect();
            if (rect.top < 0) {
                descriptionRef.current.scrollIntoView({ block: 'start', behavior: 'instant' });
            }
        }
    };

    const showToggle = isClamped || isExpanded;

    return (
        <div
            className="flex items-start gap-xs"
            ref={descriptionRef}
        >
            <Info
                className="mt-[1px]"
                color="primary"
                size="sm"
            />
            <div className="relative min-w-0 flex-1">
                <div
                    className={cn('whitespace-pre-line', !isExpanded && 'line-clamp-2')}
                    ref={textRef}
                >
                    <BodySecondary>{safelyRender({ html: description })}</BodySecondary>
                </div>
                {showToggle && !isExpanded && (
                    <div className="pointer-events-none absolute right-0 bottom-[22px] left-0 h-[24px] bg-linear-to-t from-background-primary to-transparent" />
                )}
                {showToggle && (
                    <InlineButton
                        className="mt-2xs"
                        icon={isExpanded ? Minus : Plus}
                        iconPlacement="right"
                        onClick={handleToggle}
                        size="sm"
                    >
                        {formatMessage(['catalogPage', 'catalogDetailsZone', isExpanded ? 'readLess' : 'readMore'])}
                    </InlineButton>
                )}
            </div>
        </div>
    );
};
